import { useFinancialGroupQuery } from '@/app/services/financialGroupApi';
import Books from '@/components/project/books/Books';
import Construction from '@/components/project/construction/Construction';
import Consumable from '@/components/project/consumable/Consumable';
import Equipment from '@/components/project/equipment/Equipment';
import HumanResources from '@/components/project/humanResources/HumanResources';
import RelatedService from '@/components/project/relatedService/RelatedService';
import ThirdPartyServices from '@/components/project/thirdServices/ThirdServices';
import Training from '@/components/project/training/Training';
import Travel from '@/components/project/travel/Travel';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { useFinancialSubGroup } from '@/context/FinancialSubGroup';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

function ProjectsDetails() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { code, category } = useParams();
  const { t } = useTranslation('equipment');

  const { selectedFinancialGroup, setSelectedFinancialGroup } = useFinancialGroup();
  const { setSelectedFinancialSubGroup } = useFinancialSubGroup();

  const { data } = useFinancialGroupQuery();
  const FinancialGroup = data?.filter((item) => item.costTracking === false);

  useEffect(() => {
    if (FinancialGroup && category) {
      const found = FinancialGroup.find((c) => c.name === category);
      if (found) {
        setSelectedFinancialGroup(found);
      }
    } else if (FinancialGroup && code) {
      navigate(`/projects/${code}/Equipamentos`, { replace: true });
    }
  }, [category, code, FinancialGroup, navigate, setSelectedFinancialGroup]);

  const handleCategoryChange = (e: SelectChangeEvent<string>) => {
    const selectedCode = e.target.value;
    const selected = FinancialGroup && FinancialGroup.find((c) => c.code === selectedCode);
    if (selected) {
      setSelectedFinancialGroup(selected);
      setSelectedFinancialSubGroup(undefined);
      if (code) {
        navigate(`/projects/${code}/${encodeURIComponent(selected.name)}`);
      }
    }
  };

  const renderCategoriaComponent = () => {
    switch (selectedFinancialGroup?.code) {
      case 'EQUIPMENTS':
        return <Equipment group={selectedFinancialGroup} />;
      case 'CONSTRUCTION':
        return <Construction group={selectedFinancialGroup} />;
      case 'RH':
        return <HumanResources groupCode={selectedFinancialGroup.id} />;
      case 'BOOKS':
        return <Books group={selectedFinancialGroup} />;
      case 'CONSUMABLES':
        return <Consumable group={selectedFinancialGroup} />;
      case 'TRAVEL':
        return <Travel group={selectedFinancialGroup} />;
      case 'TRAINING':
        return <Training group={selectedFinancialGroup} />;
      case 'THIRDSERVICES':
        return <ThirdPartyServices group={selectedFinancialGroup} />;
      case 'RELATEDSERVICES':
        return <RelatedService group={selectedFinancialGroup} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Typography component="p" variant="h1">
          {t('category')}
        </Typography>
        <FormControl sx={{ width: theme.spacing(30) }}>
          <Select
            displayEmpty
            value={selectedFinancialGroup?.code}
            onChange={handleCategoryChange}
            sx={{ height: 32, fontSize: 14 }}
            renderValue={(selected) => {
              if (!selected) return category;
              const found = FinancialGroup?.find((item) => item.code === selected);
              return found ? t(found.name) : category;
            }}
          >
            {FinancialGroup &&
              FinancialGroup.map((item) => (
                <MenuItem key={item.id} value={item.code} sx={{ fontSize: 13 }}>
                  {t(item.name)}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>

      {/* Renders the component based on the URL header */}
      <Box sx={{ mt: 4 }}>{renderCategoriaComponent()}</Box>
    </Box>
  );
}

export default ProjectsDetails;
